import React, { useEffect, useState, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { toast } from "sonner";
import { LogOut, Trash2, RefreshCw, Mail, Phone, Calendar, MapPin } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { SITE } from "../data/site";

const TYPE_LABEL = { booking: "Booking", quote: "Quote", contact: "Contact" };
const STATUS_OPTIONS = ["new", "contacted", "booked", "closed"];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, by_type: {}, by_status: {} });
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterType) params.append("type", filterType);
      if (filterStatus) params.append("status", filterStatus);
      const [{ data: ld }, { data: st }] = await Promise.all([
        api.get(`/admin/leads?${params}`),
        api.get("/admin/stats"),
      ]);
      setLeads(ld.leads);
      setStats(st);
    } catch (e) {
      toast.error("Failed to load leads");
    } finally {
      setLoading(false);
    }
  }, [filterType, filterStatus]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/leads/${id}`, { status });
      toast.success(`Marked ${status}`);
      load();
    } catch { toast.error("Update failed"); }
  };

  const deleteLead = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    try {
      await api.delete(`/admin/leads/${id}`);
      toast.success("Deleted");
      setSelected(null);
      load();
    } catch { toast.error("Delete failed"); }
  };

  const handleLogout = () => {
    logout();
    nav("/admin/login");
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="admin-dashboard">
      <header className="border-b border-white/10 bg-black/60 backdrop-blur-xl sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={SITE.logo} alt="USA Limo" className="h-10 w-10 object-contain" />
            <div>
              <div className="label-eyebrow">Control Room</div>
              <div className="font-display text-xl">Leads Dashboard</div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-xs text-white/50 hidden sm:block">{user?.email}</span>
            <button onClick={handleLogout} className="btn-outline text-xs" data-testid="admin-logout">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10">
        {/* Stats grid */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-10">
          <Stat label="Total Leads" value={stats.total} testId="stat-total" />
          <Stat label="New" value={stats.new} accent testId="stat-new" />
          <Stat label="Bookings" value={stats.by_type?.booking || 0} testId="stat-booking" />
          <Stat label="Quotes" value={stats.by_type?.quote || 0} testId="stat-quote" />
          <Stat label="Contacts" value={stats.by_type?.contact || 0} testId="stat-contact" />
        </div>

        <div className="flex flex-wrap items-center gap-3 mb-6">
          <select className="lux-input w-auto" value={filterType} onChange={(e) => setFilterType(e.target.value)} data-testid="filter-type">
            <option value="">All Types</option>
            <option value="booking">Booking</option>
            <option value="quote">Quote</option>
            <option value="contact">Contact</option>
          </select>
          <select className="lux-input w-auto" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} data-testid="filter-status">
            <option value="">All Statuses</option>
            {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <button onClick={load} className="btn-outline text-xs" data-testid="reload-btn">
            <RefreshCw size={14} /> Reload
          </button>
          {loading && <span className="text-white/50 text-sm">Loading…</span>}
        </div>

        <div className="grid lg:grid-cols-12 gap-6">
          <div className="lg:col-span-7 lux-card overflow-hidden">
            <table className="w-full text-sm" data-testid="leads-table">
              <thead className="bg-white/5">
                <tr className="text-left text-xs uppercase tracking-widest text-white/50">
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3 hidden md:table-cell">Email</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 hidden lg:table-cell">Created</th>
                </tr>
              </thead>
              <tbody>
                {leads.length === 0 && !loading && (
                  <tr><td colSpan="5" className="px-4 py-10 text-center text-white/50">No leads yet.</td></tr>
                )}
                {leads.map((l) => (
                  <tr
                    key={l.id}
                    onClick={() => setSelected(l)}
                    className={`border-t border-white/5 cursor-pointer hover:bg-white/5 ${selected?.id === l.id ? "bg-white/[0.06]" : ""}`}
                    data-testid={`lead-row-${l.id}`}
                  >
                    <td className="px-4 py-3 text-white/80">
                      <span className="text-[10px] tracking-widest uppercase px-2 py-1 border border-white/15 mr-2">{TYPE_LABEL[l.type]}</span>
                    </td>
                    <td className="px-4 py-3 text-white">{l.full_name || "—"}</td>
                    <td className="px-4 py-3 text-white/60 hidden md:table-cell">{l.email}</td>
                    <td className="px-4 py-3"><StatusBadge status={l.status} /></td>
                    <td className="px-4 py-3 text-white/50 text-xs hidden lg:table-cell">{new Date(l.created_at).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="lg:col-span-5">
            {selected ? (
              <div className="lux-card p-6 sticky top-28" data-testid="lead-detail">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="label-eyebrow">{TYPE_LABEL[selected.type]}</div>
                    <h3 className="font-display text-2xl text-white">{selected.full_name || "Unknown"}</h3>
                  </div>
                  <StatusBadge status={selected.status} />
                </div>
                <div className="space-y-2.5 text-sm">
                  <div className="flex items-start gap-2 text-white/80"><Mail size={14} className="text-[#d4af6a] mt-1" />{selected.email || "—"}</div>
                  <div className="flex items-start gap-2 text-white/80"><Phone size={14} className="text-[#d4af6a] mt-1" />{selected.phone || "—"}</div>
                  <div className="flex items-start gap-2 text-white/60 text-xs"><Calendar size={14} className="text-[#d4af6a] mt-0.5" />{new Date(selected.created_at).toLocaleString()}</div>
                </div>
                <div className="border-t border-white/10 my-5"></div>
                <div className="text-xs text-white/50 uppercase tracking-widest mb-3">Submission</div>
                <pre className="text-xs text-white/75 bg-black/40 p-4 overflow-auto max-h-72 whitespace-pre-wrap break-words">
                  {JSON.stringify(selected.data, null, 2)}
                </pre>

                <div className="border-t border-white/10 my-5"></div>
                <div className="text-xs text-white/50 uppercase tracking-widest mb-3">Update Status</div>
                <div className="flex flex-wrap gap-2">
                  {STATUS_OPTIONS.map((s) => (
                    <button
                      key={s}
                      onClick={() => updateStatus(selected.id, s)}
                      className={`text-[10px] tracking-widest uppercase px-3 py-2 border ${selected.status === s ? "border-[#9B1B30] bg-[#9B1B30]/20 text-white" : "border-white/15 text-white/70 hover:text-white"}`}
                      data-testid={`status-btn-${s}`}
                    >
                      {s}
                    </button>
                  ))}
                  <button onClick={() => deleteLead(selected.id)} className="ml-auto btn-outline text-xs border-[#9B1B30] text-[#9B1B30] hover:bg-[#9B1B30] hover:text-white" data-testid="delete-lead">
                    <Trash2 size={12} /> Delete
                  </button>
                </div>
              </div>
            ) : (
              <div className="lux-card p-10 text-center text-white/50">
                Select a lead to view details.
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

function Stat({ label, value, accent, testId }) {
  return (
    <div className={`lux-card p-5 ${accent ? "border-[#9B1B30]/60" : ""}`} data-testid={testId}>
      <div className="text-xs uppercase tracking-widest text-white/50">{label}</div>
      <div className={`font-display text-3xl mt-1 ${accent ? "text-[#9B1B30]" : "text-white"}`}>{value}</div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    new: "border-[#9B1B30] text-[#9B1B30]",
    contacted: "border-amber-500/60 text-amber-400",
    booked: "border-emerald-500/60 text-emerald-400",
    closed: "border-white/30 text-white/50",
  };
  return (
    <span className={`text-[10px] tracking-widest uppercase px-2 py-1 border ${map[status] || map.new}`}>{status}</span>
  );
}
